import {useNavigate} from "@tanstack/react-router";
import {useAuthState} from "react-firebase-hooks/auth";

import {useGetUserPolls, Poll} from "@/hooks/useGetUserPolls";
import {Feed} from "../../components/Feed";
import {auth} from "@/features/api";
import Spinner from "@/components/Spinner";
import ErrorImage from "@/components/ErrorImage";
import {PushNotificationsContext} from "@/layouts/AppLayout/push-notifications-context";
import {useContext, useEffect} from "react";
import VerifyUserAge from "../../components/VerifyUserAge";

export const FeedPage = () => {
  const navigate = useNavigate();
  const [user, isAuthStateLoading, isAuthStateError] = useAuthState(auth);
  const getUserPolls = useGetUserPolls(user?.uid);
  const pushNotifications = useContext(PushNotificationsContext);

  const isLoading = isAuthStateLoading || getUserPolls.isLoading;
  const isError = isAuthStateError || getUserPolls.isError;

  // `refetch` is referentially stable in React Query, so depending on it does
  // not re-run this on every render the way the whole query object would.
  const {refetch: refetchUserPolls} = getUserPolls;
  useEffect(() => {
    if (!pushNotifications.messageCount) return;

    // refetch the user polls when a new message arrives
    refetchUserPolls();
  }, [pushNotifications.messageCount, refetchUserPolls]);

  const navigateToPoll = (poll: Poll) => {
    // open the external link in a new tab
    if (poll.externalLink) {
      window.open(poll.externalLink, "_blank");
      return;
    }

    // navigate to the poll page
    navigate({to: `/poll/${poll.slug}`});
  };

  if (isError)
    return <ErrorImage error={isAuthStateError || getUserPolls.error} />;

  return (
    <>
      {isLoading || !getUserPolls.data ? (
        <Spinner />
      ) : (
        <Feed polls={getUserPolls.data} navigateToPoll={navigateToPoll} />
      )}
      <VerifyUserAge user={user} />
    </>
  );
};
